import moment from "moment";

// pnr comes back as a JSON string, e.g. "{\"KUL-BLR\":\"NSIAJW\"}" — flatten it to "NSIAJW" / "NSIAJW,ABC123"
export const getPnrCodes = (pnrRaw) => {
  if (!pnrRaw) return "";
  try {
    const parsed = JSON.parse(pnrRaw);
    if (parsed && typeof parsed === "object") {
      return Object.values(parsed).filter(Boolean).join(",");
    }
    return String(parsed);
  } catch (e) {
    return pnrRaw;
  }
};

// Left/right pads (and truncates) a value to a fixed field width, as required by the BCBP layout.
export const padField = (value, len, { padChar = " ", padStart = false } = {}) => {
  const s = (value ?? "").toString().toUpperCase().slice(0, len);
  return padStart ? s.padStart(len, padChar) : s.padEnd(len, padChar);
};

// Julian day-of-year (1-366), zero-padded to 3 digits, as required by the "Date of Flight" field.
export const getJulianDate = (dateStr) => {
  if (!dateStr) return "001";
  return padField(moment(dateStr).dayOfYear(), 3, { padChar: "0", padStart: true });
};

// Cabin class -> single-letter compartment code.
export const getCompartmentCode = (cabinClass) => {
  switch ((cabinClass || "").toUpperCase()) {
    case "BUSINESS": return "C";
    case "FIRST": return "F";
    case "PREMIUM_ECONOMY":
    case "PREMIUMECONOMY": return "W";
    default: return "Y"; // ECONOMY
  }
};

// Builds the mandatory-items BCBP string per the syntax:
// <Format Code><Legs Encoded><Passenger Name><E-Ticket Ind><Operating Carrier PNR>
// <From City><To City><Operating Carrier Designator><Flight Number><Julian Date>
// <Compartment><Seat Number><Check-in Sequence><Passenger Status><Variable Field Size>
//
// Seat number, check-in sequence and passenger status aren't returned by this booking API
// (they only exist once the traveler has actually checked in at the airport), so those three
// fields are zero-filled/defaulted below rather than fabricated.
export const buildBoardingPassBarcode = (bookingDetails, traveler) => {
  if (!traveler || !bookingDetails) return "";

  const leg = bookingDetails.dcFlightLegs?.[0];
  const seg = leg?.dcSegments?.[0];
  const pnrCode = getPnrCodes(bookingDetails.pnr).split(",")[0];

  const formatCode = "M";
  const legsEncoded = padField(bookingDetails.dcFlightLegs?.length || 1, 1, { padChar: "0", padStart: true });
  const passengerName = padField(`${traveler.lastName || ""}/${traveler.firstName || ""}`, 20);
  const eTicketIndicator = "E";
  const operatingCarrierPnr = padField(pnrCode, 7);
  const fromCityCode = padField(seg?.tFDepartureData?.airportCode, 3);
  const toCityCode = padField(seg?.tFArrivalData?.airportCode, 3);
  const operatingCarrierDesignator = padField(seg?.tFAirlineData?.airlineCode, 3);
  const flightNumber = padField(
    padField((seg?.tFAirlineData?.flightNumber || "").replace(/\D/g, ""), 4, { padChar: "0", padStart: true }),
    5
  ); // 4-digit flight number + 1 trailing spare char
  const julianDate = getJulianDate(bookingDetails.departureDate);
  const compartmentCode = getCompartmentCode(bookingDetails.cabinClass);
  const seatNumber = "0000"; // not assigned pre check-in
  const checkinSequence = "00000"; // not assigned pre check-in
  const passengerStatus = "0"; // 0 = not checked in
  const variableFieldSize = "00"; // no optional/conditional data appended

  return [
    formatCode,
    legsEncoded,
    passengerName,
    eTicketIndicator,
    operatingCarrierPnr,
    fromCityCode,
    toCityCode,
    operatingCarrierDesignator,
    flightNumber,
    julianDate,
    compartmentCode,
    seatNumber,
    checkinSequence,
    passengerStatus,
    variableFieldSize,
  ].join("");
};

// Fixed field widths for the BCBP mandatory-items string, in the same order buildBoardingPassBarcode
// writes them — used to parse the barcode value back out, the way a boarding-pass scanner would.
export const BCBP_FIELDS = [
  ["formatCode", 1],
  ["legsEncoded", 1],
  ["passengerName", 20],
  ["eTicketIndicator", 1],
  ["pnr", 7],
  ["from", 3],
  ["to", 3],
  ["carrier", 3],
  ["flightNumber", 5],
  ["julianDate", 3],
  ["compartment", 1],
  ["seat", 4],
  ["checkinSequence", 5],
  ["passengerStatus", 1],
  ["variableFieldSize", 2],
];

// Reverses buildBoardingPassBarcode: splits the encoded string back into its fields.
export const decodeBoardingPassBarcode = (value) => {
  if (!value) return null;
  let offset = 0;
  const decoded = {};
  BCBP_FIELDS.forEach(([field, len]) => {
    decoded[field] = value.substr(offset, len).trim();
    offset += len;
  });
  return decoded;
};

// Human-readable summary shown alongside the barcode — decoded from the same encoded string the
// barcode carries (passenger name, PNR, departure/arrival, carrier/flight, date of flight), the
// way this information would come back when the barcode is scanned.
export const getBoardingPassSummary = (bookingDetails, traveler) => {
  const decoded = decodeBoardingPassBarcode(buildBoardingPassBarcode(bookingDetails, traveler));
  if (!decoded) return null;

  const [lastName, firstName] = decoded.passengerName.split("/");
  const year = bookingDetails.departureDate ? moment(bookingDetails.departureDate).year() : moment().year();
  const julianDateNum = parseInt(decoded.julianDate, 10) || 1;
  const flightDate = moment(`${year}-01-01`, "YYYY-MM-DD").dayOfYear(julianDateNum);
  const flightNumberNum = parseInt(decoded.flightNumber, 10);

  return {
    name: [firstName, lastName].filter(Boolean).join(" ") || decoded.passengerName,
    pnr: decoded.pnr,
    from: decoded.from,
    to: decoded.to,
    carrierFlight: `${decoded.carrier}${Number.isNaN(flightNumberNum) ? "" : flightNumberNum}`,
    date: flightDate.isValid() ? flightDate.format("DD MMM YYYY") : "",
  };
};

// Plain-text payload encoded into the QR code — deliberately NOT the fixed-width BCBP
// string above. A generic phone camera / QR scanner just shows back whatever text is
// encoded, so this is written to already be the readable result: passenger name, PNR,
// departure/arrival airports, airline, flight number and date of flight.
export const buildBoardingPassScanText = (bookingDetails, traveler) => {
  if (!traveler || !bookingDetails) return "";

  const leg = bookingDetails.dcFlightLegs?.[0];
  const seg = leg?.dcSegments?.[0];
  const pnrCode = getPnrCodes(bookingDetails.pnr).split(",")[0];

  const name = `${traveler.firstName || ""} ${traveler.lastName || ""}`.trim().toUpperCase();
  const from = seg?.tFDepartureData?.airportCode || "";
  const to = seg?.tFArrivalData?.airportCode || "";
  const airline = seg?.tFAirlineData?.airline || seg?.tFAirlineData?.airlineCode || "";
  const flightNumber = `${seg?.tFAirlineData?.airlineCode || ""}${(seg?.tFAirlineData?.flightNumber || "").toString().replace(/\D/g, "")}`;
  const date = bookingDetails.departureDate ? moment(bookingDetails.departureDate).format("DD MMM YYYY") : "";

  return [
    name && `Passenger: ${name}`,
    pnrCode && `PNR: ${pnrCode}`,
    from && to && `${from} → ${to}`,
    airline && `Airline: ${airline}`,
    flightNumber && `Flight: ${flightNumber}`,
    date && `Date: ${date}`,
  ].filter(Boolean).join("\n");
};
